import { formatDistanceToNow, isThisWeek, isToday, isYesterday, parseISO, isThisMonth, subWeeks, isWithinInterval, startOfWeek, endOfWeek, subMonths } from "date-fns";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../../../assets/scss/scrollbar.css";
import { useChat } from "../../../context/chatContext";
import { useChatHistory } from "../../../context/chatHistoryContext";
import { useAuth } from "../../../context/authContext";
import { useCases } from "../../../utils/constants";
import MessageIcon from "../../../assets/svg/messageIcon";
import BulbIcon from "../../../assets/svg/ideaBulb";
import EditTextIcon from "../../../assets/svg/editTextIcon";
import ShareIcon from "../../../assets/svg/shareIcon";
import ThumbsUpIcon from "../../../assets/svg/thumbsUpIcon";
import ThumbsDownIcon from "../../../assets/svg/thumbsDownIcon";
import tickIcon from "../../../assets/img/tick-icon.png";
import searchIcon from "../../../assets/img/search-blue.png";
import createAxiosInstance from "../../../utils/axiosConfig";
import ShareModal from "../modal/shareModal";
import Alert from "../alerts/alert";
import ShareHistoryIcon from "../../../assets/svg/shareHistoryIcon";
import ThumbsDownHistory from "../../../assets/svg/thumbsDownHistory";
import ThumbsUpHistory from "../../../assets/svg/thumbsUpHistory";

interface Conversation {
	id: string;
	user_id: string;
	chat_id: number;
	title?: string;
	title_id?: number;
	title_status?: string;
	user_email?: string;
	positive_feedback_count?: number;
	negative_feedback_count?: number;
	conversation: Array<{
		conversation_id: number;
		question: string;
		response: string;
		label: string;
		date: string;
	}>;
}
interface ChatHistoryProps {
	onNavigate: (path: string) => void;
}

interface AlertType {
	message: string;
	type: "success" | "danger";
	show: boolean;
}

// Helper function for robust UTC date parsing
const parseUTCDate = (dateString: string): Date => {
	// Split date and time parts to check for timezone info only in the time part
	const timePart = dateString.split('T')[1] || '';
	const hasTimezoneInfo = timePart.includes('Z') || timePart.includes('+') || timePart.includes('-');
	
	// If the time part has timezone info, use it as-is
	if (hasTimezoneInfo) {
		return new Date(dateString);
	}
	
	// Parse the date string components manually for UTC interpretation
	// Expected format: YYYY-MM-DDTHH:mm:ss
	const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})$/);
	if (!match) {
		// Fallback to regular Date parsing if format doesn't match
		console.warn('Unexpected date format:', dateString);
		return new Date(dateString);
	}
	
	const [, year, month, day, hour, minute, second] = match;
	
	// Create UTC date using Date.UTC() (month is 0-indexed in Date.UTC)
	const utcTime = Date.UTC(
		parseInt(year, 10),
		parseInt(month, 10) - 1, // Month is 0-indexed
		parseInt(day, 10),
		parseInt(hour, 10),
		parseInt(minute, 10),
		parseInt(second, 10)
	);
	
	// Return a Date object that represents the UTC time in local timezone
	return new Date(utcTime);
};

// Helper functions for date categorization
const isLastWeek = (date: Date) => {
	const now = new Date();
	const lastWeekStart = startOfWeek(subWeeks(now, 1));
	const lastWeekEnd = endOfWeek(subWeeks(now, 1));
	return isWithinInterval(date, { start: lastWeekStart, end: lastWeekEnd });
};

const isLastMonth = (date: Date) => {
	const now = new Date();
	const lastMonth = subMonths(now, 1);
	return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
};

const categorizeConversations = (conversations: Conversation[]) => {
	const today: Conversation[] = [];
	const yesterday: Conversation[] = [];
	const thisWeek: Conversation[] = [];
	const lastWeek: Conversation[] = [];
	const thisMonth: Conversation[] = [];
	const lastMonth: Conversation[] = [];
	const older: Conversation[] = [];
	
	conversations.forEach((conversation, index) => {
		// Allow conversations with at least 1 message instead of requiring > 1
		if (conversation.conversation.length > 1) {
			const dateString = conversation.conversation[0].date;
			
			// Use the robust UTC date parsing function
			const date = parseUTCDate(dateString);
			
			if (isToday(date)) {
				today.push(conversation);
			} else if (isYesterday(date)) {
				yesterday.push(conversation);
			} else if (isThisWeek(date)) {
				thisWeek.push(conversation);
			} else if (isLastWeek(date)) {
				lastWeek.push(conversation);
			} else if (isThisMonth(date)) {
				thisMonth.push(conversation);
			} else if (isLastMonth(date)) {
				lastMonth.push(conversation);
			} else {
				// Add conversations older than last month to "older" category
				older.push(conversation);
			}
		}
	});

	return { today, yesterday, thisWeek, lastWeek, thisMonth, lastMonth, older };
};

const ChatHistory: React.FC<ChatHistoryProps> = ({ onNavigate }) => {
	const { resumeChat } = useChat();
	const { isOpen, conversations, loading, closeChatHistory, fetchConversations, updateConversationTitle } = useChatHistory();
	const { logout } = useAuth();
	const navigate = useNavigate();
	
	const [visibleToday, setVisibleToday] = useState(3);
	const [visibleYesterday, setVisibleYesterday] = useState(3);
	const [visibleThisWeek, setVisibleThisWeek] = useState(3);
	const [visibleLastWeek, setVisibleLastWeek] = useState(3);
	const [visibleThisMonth, setVisibleThisMonth] = useState(3);
	const [visibleLastMonth, setVisibleLastMonth] = useState(3);
	const [visibleOlder, setVisibleOlder] = useState(3);
	const [showScrollTop, setShowScrollTop] = useState(false);

	const [editingChat, setEditingChat] = useState<string | null>(null);
  	const [editTitle, setEditTitle] = useState<string>("");
	const [originalTitle, setOriginalTitle] = useState<string>("");
	const [titleSaveLoading, setTitleSaveLoading] = useState<boolean>(false);
	
	// Share functionality states
	const [showShareModal, setShowShareModal] = useState(false);
	const [sharingChatId, setSharingChatId] = useState<number | null>(null);
	const [alert, setAlert] = useState<AlertType | null>(null);
	
	const { today, yesterday, thisWeek, lastWeek, thisMonth, lastMonth, older } = categorizeConversations(conversations);


	const formatTimeAgo = (dateString: string) => {
		// Use the robust UTC date parsing function
		const date = parseUTCDate(dateString);
		
		if (isToday(date)) {
		  const diffMs = new Date().getTime() - date.getTime();
		  const diffMins = Math.round(diffMs / 60000);
		  
		  if (diffMins < 60) {
			return `${diffMins}min ago`;
		  } else {
			const diffHrs = Math.floor(diffMins / 60);
			return `${diffHrs}hr ago`;
		  }
		} else if (isYesterday(date)) {
		  return "yesterday";
		} else {
		  return new Intl.DateTimeFormat("en-us", {
			dateStyle: "medium",
		  }).format(date);
		}
	};
	  
	// Handle alert messages
	const handleAlert = (message: string, type: "success" | "danger") => {
		setAlert({
			message,
			type,
			show: true
		});
	};

	// Handle share functionality
	const handleShare = (chatId: number) => {
		setSharingChatId(chatId);
		// Close chat history first
		closeChatHistory();
		// Show share modal after a small delay to ensure smooth transition
		setTimeout(() => {
			setShowShareModal(true);
		}, 300);
	};

	// Handle share submission
	const handleShareSubmit = async (email: string, subject: string, message: string) => {
		if (!sharingChatId) return;
		
		try {
			const token = localStorage.getItem('token');
			const response = await fetch(
				process.env.REACT_APP_SERVER_URL + "/share-via-email" ||
				"https://chat.1ciso.io/backend/share-via-email",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify({
						chat_id: sharingChatId.toString(),
						receiver_email: email,
						subject: subject,
						message: message,
					}),
				}
			);

			if (response.status === 401 || response.status === 403) {
				logout();
				navigate("/");
				return;
			}

			if (!response.ok) {
				handleAlert("Failed to share via email. Please try again.", "danger");
				return;
			}

			console.log("Share email sent successfully");
			handleAlert("Email shared successfully!", "success");
		} catch (error) {
			console.error("Error sharing via email:", error);
			handleAlert("Failed to share via email. Please try again.", "danger");
		}
	};

	useEffect(() => {
		if (isOpen) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}

		return () => {
			document.body.classList.remove("overflow-hidden");
		};
	}, [isOpen]);

	// useEffect for scroll handling
	useEffect(() => {
		const handleScroll = (e: Event) => {
			const element = e.target as HTMLElement;
			if (element.scrollTop > 100) {
				setShowScrollTop(true);
			} else {
				setShowScrollTop(false);
			}
		};

		const offcanvasBody = document.querySelector(".offcanvas-body");
		offcanvasBody?.addEventListener("scroll", handleScroll);

		return () => {
			offcanvasBody?.removeEventListener("scroll", handleScroll);
		};
	}, [visibleToday, visibleYesterday, visibleThisWeek, visibleLastWeek, visibleThisMonth, visibleLastMonth, visibleOlder]);


	const handleEditClick = (e: React.MouseEvent, convId: string, title: string) => {
		e.preventDefault();
		e.stopPropagation();
		setEditingChat(convId);
		setEditTitle(title);
		setOriginalTitle(title);
	  };
	  
	  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!titleSaveLoading) {
			setEditTitle(e.target.value);
		}
	  };
	  
	  const handleTitleSave = async (e: React.FormEvent, convId: string) => {
		e.preventDefault();
		
		// Check if title has changed
		if (editTitle.trim() === originalTitle.trim()) {
			// No changes, just close edit mode
			setEditingChat(null);
			return;
		}
		
		// Find the conversation object to get the correct IDs
		const conversation = conversations.find(conv => conv.id === convId);
		if (!conversation || conversation.conversation.length === 0) {
			console.error("Conversation not found or empty");
			setEditingChat(null);
			return;
		}
		
		// Set loading state
		setTitleSaveLoading(true);
		
		// Title has changed, send PUT request
		try {
			const payload = {
				chat_id: conversation.chat_id,
				conversation_id: conversation.conversation[0].conversation_id,
				title: editTitle.trim()
			};
			
			const response = await createAxiosInstance().put("/chat/title", payload);
			
			if (response.status === 200 && response.data) {
				console.log("Title updated successfully", response.data);
				
				// Update the specific conversation title from API response
				const updatedTitle = response.data.data?.title || response.data.title;
				updateConversationTitle(convId, updatedTitle);
				
				// Close edit mode
				setEditingChat(null);
			}
		} catch (error) {
			console.error("Error updating title:", error);
			// You might want to show an error message to the user
			setEditingChat(null);
		} finally {
			setTitleSaveLoading(false);
		}
	  };
	  
	  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Escape" && !titleSaveLoading) {
		  setEditingChat(null);
		}
	  };


	const scrollToTop = () => {
		const offcanvasBody = document.querySelector(".offcanvas-body");
		offcanvasBody?.scrollTo({ top: 0, behavior: "smooth" });
	};

	const handleResumeChat = (conversation: any) => {
		const label = conversation.conversation[1]?.label.split("_");
		const useCaseId = Number(label[label.length - 1].split(".")[0]);

		const useCase = useCases.find((uc) => uc.value === useCaseId);
		const useCasePath = useCase ? useCase.route : "/default-path"; // Fallback to a default path if not found

		resumeChat(conversation, () => {
			onNavigate(useCasePath);
			closeChatHistory();
		});
	};

	const chatTitle = (conv: Conversation, useCaseObj: any) => {
		// Check if a dedicated title exists and use it
		if (conv.title && conv.title_status) {
			return conv.title;
		}

		// Fall back to existing title mechanism
		let label = conv?.conversation[1]?.label.split("_");
		let useCase = Number(label[label.length - 1]);

		if (label[label.length - 1].split(".").length >= 2) {
			useCase = Number(label[label.length - 1].split(".")[0]);
		}
		if (useCase == useCaseObj?.value) {
			return `${conv.conversation[1].question.slice(
				0,
				100
			)}`;
		}
	};

	const chatUseCase = (conv: Conversation, useCaseObj: any) =>{
		let label = conv?.conversation[1]?.label.split("_");
		let useCase = Number(label[label.length - 1]);

		if (label[label.length - 1].split(".").length >= 2) {
			useCase = Number(label[label.length - 1].split(".")[0]);
		}
		if (useCase == useCaseObj?.value) {
			return `${useCaseObj?.name}`;
		}
	}

	const renderConversations = (
		category: Conversation[],
		visibleCount: number,
		setVisibleCount: (count: number) => void
	) => (
		<>
			{category.slice(0, visibleCount).map((conv, index) => {
				// Check if dedicated title exists, otherwise use existing mechanism
				const title = conv.title && conv.title_status 
					? conv.title 
					: useCases.map((value) => chatTitle(conv, value)).filter(Boolean)[0] || "";
        
				return ( <li
						key={index}
						className="list-group-item text-truncate my-2 mx-4 position-relative"
						data-bs-dismiss="offcanvas"
						aria-label="Close"
						style={{ cursor: "default", backgroundColor: 'rgba(212, 212, 212, 0.2)', color:"black"}}
					>
						<div className="text-end" style={{  fontSize: "12px", color: "#5376F0" }}>
							{formatTimeAgo(conv.conversation[0].date)}
						</div>

						{editingChat === conv.id ? (
							<form onSubmit={(e) => handleTitleSave(e, conv.id)} className="mt-2 mb-1 d-flex justify-content-start">
								<input
								type="text"
								className="form-control w-50"
								value={editTitle}
								onChange={handleTitleChange}
								onKeyDown={handleTitleKeyDown}
								autoFocus
								onClick={(e) => e.stopPropagation()}
								disabled={titleSaveLoading}
								style={{ 
									opacity: titleSaveLoading ? 0.6 : 1,
									cursor: titleSaveLoading ? 'not-allowed' : 'text'
								}}
								/>
								<div className="btn" onClick={(e) => handleTitleSave(e, conv.id)} style={{ pointerEvents: titleSaveLoading ? 'none' : 'auto' }}>
									{titleSaveLoading ? (
										<div className="spinner-border spinner-border-sm" role="status" style={{ width: '16px', height: '16px' }}>
											<span className="visually-hidden">Loading...</span>
										</div>
									) : (
										<img src={tickIcon}/>
									)}
								</div>
							</form>
						) : (
						<>
							<a href="#" onClick={() => handleResumeChat(conv)} style={{ marginTop: 10, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
								{title}
							</a>
							<span 
							className="position-absolute mx-2" 
							style={{ cursor: "pointer" }}
							onClick={(e) => handleEditClick(e, conv.id, title)}
							>
							<EditTextIcon />
							</span>
						</>
						)}
						
						<div className="d-flex justify-content-between">
							<p className="text-truncate px-4 mt-2 mb-2" style={{fontSize: 12, borderRadius: 12, background: "#A7CCFF", maxWidth:'fit-content'}}>{useCases.map((value) => chatUseCase(conv, value))}</p>
							<div className="d-flex flex-row gap-2 align-items-center">
								{/* Thumbs Up with Count */}
								<div className="d-flex align-items-center gap-1" style={{
									// opacity: 0.7,
									backgroundColor: "white",
									border: "1px solid #2AC141",
									borderRadius: "1.875rem",
									padding: "2px 6px",
									cursor: 'default'
								}}>
									<div style={{bottom: 1.5, position: "relative", paddingRight: 4}}>
										<ThumbsUpHistory />
									</div>
									<span style={{
										fontSize: "0.75rem", 
										color: "#0C099C", 
										fontWeight: "500",
										paddingRight: 4
									}}>
										{conv.positive_feedback_count || 0}
									</span>
								</div>
								
								{/* Thumbs Down with Count */}
								<div className="d-flex align-items-center gap-1" style={{
									// opacity: 0.7,
									backgroundColor: "white",
									border: "1px solid #FF453A",
									borderRadius: "1.875rem",
									padding: "2px 6px",
									cursor: 'default'
								}}>
									<div style={{bottom: 0, position: "relative", paddingRight: 4}}>
										<ThumbsDownHistory />
									</div>
									<span style={{
										fontSize: "0.75rem", 
										color: "#0C099C", 
										fontWeight: "500",
										paddingRight: 4
									}}>
										{conv.negative_feedback_count || 0}
									</span>
								</div>
								
								<button
									className="btn btn-link"
									onClick={(e) => {
										e.stopPropagation();
										handleShare(conv.chat_id);
									}}
									style={{
										backgroundColor: "transparent",
										border: "none",
										padding: "0",
										display: "flex",
										alignItems: "center",
										justifyContent: "center"
									}}
									title="Share via email"
								>
									<ShareHistoryIcon />
								</button>
							</div>
						</div>
					</li>)
			})}
			{visibleCount < category.length && (
				<li
					className="list-group-item text-center mt-2"
					style={{ cursor: "pointer", color: "#205493", fontSize: 14 }}
					onClick={() => setVisibleCount(visibleCount + 3)}
				>
					Load More
				</li>
			)}
		</>
	);

	if (!isOpen && !showShareModal && !alert?.show) return null;
	
	return (
		<>
			{isOpen && (
				<>
					<div
						className="offcanvas-backdrop fade show"
						onClick={closeChatHistory}
					></div>
					<div
						className="offcanvas offcanvas-end show"
						tabIndex={-1}
					>
				<div className="offcanvas-header">
					<h5 className="offcanvas-title" id="offcanvasExampleLabel">
						<MessageIcon /> &nbsp; CHAT HISTORY
					</h5>
					{/* <button
						type="button"
						className="btn-close white"
						data-bs-dismiss="offcanvas"
						aria-label="Close"
						onClick={closeChatHistory}
						style={{color: 'white'}}
					></button> */}
				</div>
				<div className="offcanvas-body scroller">
					<div className="d-flex flex-row justify-content-start align-items-center gap-3 offcanvas-history-text py-2 px-2">
						<BulbIcon />
						<p style={{marginBottom: 0}}>Choose any one of the History topic and continue Chatting on same thread, it will auto save.</p>
					</div>
					{loading ? (
						<div className="text-center w-100 h-100 d-flex justify-content-center align-items-center">
							<div className="spinner-border">
								<span className="visually-hidden">Loading...</span>
							</div>
						</div>
					) : (
						<>
							{today.length > 0 && (
								<>
									<h6 className="text-blue mb-3 ps-3 mt-4">
										<i className="bi bi-calendar"></i> Today
										<span
											style={{
												marginLeft: "18%",
												fontSize: "0.9rem",
												fontWeight: 500,
											}}
											className="badge bg-light text-blue"
										>
											{today.length}
										</span>
									</h6>
									{/* <div className="mt-3 mx-4 w-30 position-relative">
										<input 
											className="form-control bg-white"
											placeholder="Search Name"
											style={{borderRadius: 22, border: '1px solid #D8D8DC'}}
										/>
										<img 
											src={searchIcon} 
											alt="Search"
											className="position-absolute" 
											style={{
												right: "15px", 
												top: "50%", 
												transform: "translateY(-50%)", 
												width: "16px",
												height: "16px",
												cursor: "pointer"
											}}
										/>
									</div> */}
									<ul className="list-group list-group-flush mb-5 mt-2">
										{renderConversations(today, visibleToday, setVisibleToday)}
									</ul>
								</>
							)}
							{yesterday.length > 0 && (
								<>
									<h6 className="text-blue mb-3 ps-3 mt-3	">
										<i className="bi bi-calendar"></i> Yesterday
										<span
											style={{
												marginLeft: "10%",
												fontSize: "0.9rem",
												fontWeight: 500,
											}}
											className="badge bg-light text-blue"
										>
											{yesterday.length}
										</span>
									</h6>
									<ul className="list-group list-group-flush mb-5">
										{renderConversations(
											yesterday,
											visibleYesterday,
											setVisibleYesterday
										)}
									</ul>
								</>
							)}
							{thisWeek.length > 0 && (
								<>
									<h6 className="text-blue mb-3 ps-3 mt-3">
										<i className="bi bi-calendar"></i> This Week
										<span
											style={{
												marginLeft: "10%",
												fontSize: "0.9rem",
												fontWeight: 500,
											}}
											className="badge bg-light text-blue"
										>
											{thisWeek.length}
										</span>
									</h6>
									<ul className="list-group list-group-flush mb-5">
										{renderConversations(
											thisWeek,
											visibleThisWeek,
											setVisibleThisWeek
										)}
									</ul>
								</>
							)}
							{lastWeek.length > 0 && (
								<>
									<h6 className="text-blue mb-3 ps-3 mt-3">
										<i className="bi bi-calendar"></i> Last Week
										<span
											style={{
												marginLeft: "10%",
												fontSize: "0.9rem",
												fontWeight: 500,
											}}
											className="badge bg-light text-blue"
										>
											{lastWeek.length}
										</span>
									</h6>
									<ul className="list-group list-group-flush mb-5">
										{renderConversations(
											lastWeek,
											visibleLastWeek,
											setVisibleLastWeek
										)}
									</ul>
								</>
							)}
							{thisMonth.length > 0 && (
								<>
									<h6 className="text-blue mb-3 ps-3 mt-3">
										<i className="bi bi-calendar"></i> This Month
										<span
											style={{
												marginLeft: "8%",
												fontSize: "0.9rem",
												fontWeight: 500,
											}}
											className="badge bg-light text-blue"
										>
											{thisMonth.length}
										</span>
									</h6>
									<ul className="list-group list-group-flush mb-5">
										{renderConversations(
											thisMonth,
											visibleThisMonth,
											setVisibleThisMonth
										)}
									</ul>
								</>
							)}
							{lastMonth.length > 0 && (
								<>
									<h6 className="text-blue mb-3 ps-3 mt-3">
										<i className="bi bi-calendar"></i> Last Month
										<span
											style={{
												marginLeft: "8%",
												fontSize: "0.9rem",
												fontWeight: 500,
											}}
											className="badge bg-light text-blue"
										>
											{lastMonth.length}
										</span>
									</h6>
									<ul className="list-group list-group-flush mb-5">
										{renderConversations(
											lastMonth,
											visibleLastMonth,
											setVisibleLastMonth
										)}
									</ul>
								</>
							)}
							{older.length > 0 && (
								<>
									<h6 className="text-blue mb-3 ps-3">
										<i className="bi bi-calendar"></i> Older
										<span
											style={{
												marginLeft: "18%",
												fontSize: "0.9rem",
												fontWeight: 500,
											}}
											className="badge bg-light text-blue"
										>
											{older.length}
										</span>
									</h6>
									<ul className="list-group list-group-flush mb-5">
										{renderConversations(
											older,
											visibleOlder,
											setVisibleOlder
										)}
									</ul>
								</>
							)}
						</>
					)}

					{showScrollTop && (
						<button
							className="btn btn-link bi bi-arrow-up-circle-fill scroll-to-top"
							aria-label="Scroll to top"
							onClick={scrollToTop}
							style={{
								cursor: "pointer",
								float: "right",
								color: "#205493",
								fontSize: "18px",
							}}
						></button>
					)}
				</div>
			</div>
			</>
			)}
			
			{/* Share Modal */}
			<ShareModal
				show={showShareModal}
				onClose={() => {
					setShowShareModal(false);
					setSharingChatId(null);
				}}
				onSubmit={handleShareSubmit}
				title="Share Chat History"
			/>
			
			{/* Alert */}
			{alert && (
				<Alert
					message={alert.message}
					type={alert.type}
					show={alert.show}
					onClose={() => setAlert(null)}
				/>
			)}
		</>
	);
};

export default ChatHistory;
